//******************************************************************************************
// Chatbox Component: Main chat window for chatbot users, displaying messages and indicating
// if the user is chatting with the Bot or an Admin. Allows resetting the conversation and
// toggling the chat visibility.
//******************************************************************************************

import React, { useEffect, useState, useRef } from "react";
import MessageContainer from "./message/MessageContainer";
import "./Chatbox.scss";
import { ChatbotUserMessagesContextProvider, useChatbotUserMessagesContext } from "../../context/ChatbotUserMessagesContext";
import { SPEAKER_NAME_CONSTANTS } from "../../utils/constants";
import { ChatbotSocketProvider } from "../../context/ChatbotSocketContext";

const Chatbox = () => {
  return (
    <ChatbotUserMessagesContextProvider>
      <ChatbotSocketProvider>
        <ChatboxContent />
      </ChatbotSocketProvider>
    </ChatbotUserMessagesContextProvider>
  );
};

const ChatboxContent = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [refreshConversationFn, setRefreshConversationFn] = useState(null);

  const [showFileWarning, setShowFileWarning] = useState(false);
  const [shouldRenderWarning, setShouldRenderWarning] = useState(false);

  const dragCounter = useRef(0);
  const dropTimeout = useRef(null);

  const { isOverridden, isClosed, token, messages, botActive, isWaitingForHuman, isWithinHours } = useChatbotUserMessagesContext();

  const [hasNewMessage, setHasNewMessage] = useState(false);
  const prevMessagesLength = useRef(null);
  const isInitialRender = useRef(true);

  // Detect new messages when chatbox is closed
  useEffect(() => {
    if (prevMessagesLength.current === null) {
      prevMessagesLength.current = messages.length;
      return;
    }
    if (isInitialRender.current) {
      isInitialRender.current = false;
      prevMessagesLength.current = messages.length;
      return;
    }

    if (messages.length > prevMessagesLength.current) {
      // Trigger animation if chatbox is closed
      if (!isOpen) {
        setHasNewMessage(true);
      }
    }

    prevMessagesLength.current = messages.length;
  }, [messages, isOpen]);

  useEffect(() => {
    if (showFileWarning) {
      setShouldRenderWarning(true);
    } else {
      const timeout = setTimeout(() => setShouldRenderWarning(false), 300);
      return () => clearTimeout(timeout);
    }
  }, [showFileWarning]);

  // Function to trigger a new conversation
  const handleResetChat = () => {
    if (refreshConversationFn) {
      refreshConversationFn();
    } else {
      console.error("Refresh function not set");
    }
  };
  // Dynamically calculate conversation partner and icon
  let conversationPartner = "";
  let conversationIcon = null;

  if (isWaitingForHuman) {
    // Talking to human
    conversationPartner = "Esperando Atendimento";
    conversationIcon = null;
  } else if (isOverridden) {
    // Waiting for agent
    conversationPartner = SPEAKER_NAME_CONSTANTS.ADMIN;
    conversationIcon = (
      <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 640 512" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg">
        <path d="M622.3 271.1l-115.2-45c-4.1-1.6-12.6-3.7-22.2 0l-115.2 45c-10.7 4.2-17.7 14-17.7 24.9 0 111.6 68.7 188.8 132.9 213.9 9.6 3.7 18 1.6 22.2 0C558.4 489.9 640 420.5 640 296c0-10.9-7-20.7-17.7-24.9zM496 462.4V273.3l95.5 37.3c-5.6 87.1-60.9 135.4-95.5 151.8zM224 256c70.7 0 128-57.3 128-128S294.7 0 224 0 96 57.3 96 128s57.3 128 128 128zm96 40c0-2.5.8-4.8 1.1-7.2-2.5-.1-4.9-.8-7.5-.8h-16.7c-22.2 10.2-46.9 16-72.9 16s-50.6-5.8-72.9-16h-16.7C60.2 288 0 348.2 0 422.4V464c0 26.5 21.5 48 48 48h352c6.8 0 13.3-1.5 19.2-4-54-42.9-99.2-116.7-99.2-212z"></path>
      </svg>
    );
  } else if (botActive) {
    // Talking to bot

    conversationPartner = SPEAKER_NAME_CONSTANTS.BOT;
    conversationIcon = (
      <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 640 512" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg">
        <path d="M32,224H64V416H32A31.96166,31.96166,0,0,1,0,384V256A31.96166,31.96166,0,0,1,32,224Zm512-48V448a64.06328,64.06328,0,0,1-64,64H160a64.06328,64.06328,0,0,1-64-64V176a79.974,79.974,0,0,1,80-80H288V32a32,32,0,0,1,64,0V96H464A79.974,79.974,0,0,1,544,176ZM264,256a40,40,0,1,0-40,40A39.997,39.997,0,0,0,264,256Zm-8,128H192v32h64Zm96,0H288v32h64ZM456,256a40,40,0,1,0-40,40A39.997,39.997,0,0,0,456,256Zm-8,128H384v32h64ZM640,256V384a31.96166,31.96166,0,0,1-32,32H576V224h32A31.96166,31.96166,0,0,1,640,256Z"></path>
      </svg>
    );
  } else {
    // Bot is not active and not waiting for human
    conversationPartner = "";
    conversationIcon = null;
  }

  const toggleChatbox = () => {
    if (!isOpen) {
      setHasNewMessage(false);
    }
    setIsOpen(!isOpen);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    dragCounter.current += 1;
    if (dragCounter.current === 1) {
      setShowFileWarning(true);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    dragCounter.current -= 1;
    if (dragCounter.current === 0 && dropTimeout.current === null) {
      setShowFileWarning(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    dragCounter.current = 0;
    setShowFileWarning(true);
    if (dropTimeout.current) {
      clearTimeout(dropTimeout.current);
    }
    dropTimeout.current = setTimeout(() => {
      setShowFileWarning(false);
      dropTimeout.current = null;
    }, 3000);
  };

  if (!botActive && !isWithinHours) {
    return null;
  }
  return (
    <div className={`chatbox ${isOpen ? "chatbox--open" : "chatbox--closed"}`}>
      {!isOpen && (
        <button className={`chatbox__circle-button ${hasNewMessage ? "chatbox__circle-button--alert" : ""}`} onClick={toggleChatbox}>
          <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 1024 1024" height="24" width="24" xmlns="http://www.w3.org/2000/svg">
            <path d="M464 512a48 48 0 1 0 96 0 48 48 0 1 0-96 0zm200 0a48 48 0 1 0 96 0 48 48 0 1 0-96 0zm-400 0a48 48 0 1 0 96 0 48 48 0 1 0-96 0zm661.2-173.6c-22.6-53.7-55-101.9-96.3-143.3a444.35 444.35 0 0 0-143.3-96.3C630.6 75.7 572.2 64 512 64h-2c-60.6.3-119.3 12.3-174.5 35.9a445.35 445.35 0 0 0-142 96.5c-40.9 41.3-73 89.3-95.2 142.8-23 55.4-34.6 114.3-34.3 174.9A449.4 449.4 0 0 0 112 714v152a46 46 0 0 0 46 46h152.1A449.4 449.4 0 0 0 510 960h2.1c59.9 0 118-11.6 172.7-34.3a444.48 444.48 0 0 0 142.8-95.2c41.3-40.9 73.8-88.7 96.5-142 23.6-55.2 35.6-113.9 35.9-174.5.3-60.9-11.5-120-34.8-175.6zm-151.1 438C704 845.8 611 884 512 884h-1.7c-60.3-.3-120.2-15.3-173.1-43.5l-8.4-4.5H188V695.2l-4.5-8.4C155.3 633.9 140.3 574 140 513.7c-.4-99.7 37.7-193.3 107.6-263.8 69.8-70.5 163.1-109.5 262.8-109.9h1.7c50 0 98.5 9.7 144.2 28.9 44.6 18.7 84.6 45.6 119 80 34.3 34.3 61.3 74.4 80 119 19.4 46.2 29.1 95.2 28.9 145.8-.6 99.6-39.7 192.9-110.1 262.7z"></path>
          </svg>
        </button>
      )}

      {isOpen && (
        <div className="chatbox__header">
          <h2>
            {conversationIcon} {conversationPartner}
          </h2>
          <div className="chatbox__header-buttons">
            {token && (
              <button className="chatbox__reset-button" type="button" onClick={handleResetChat}>
                Reiniciar Conversa
              </button>
            )}
            <button className="chatbox__toggle-button" type="button" onClick={toggleChatbox}>
              &times;
            </button>
          </div>
        </div>
      )}
      <div className={`chatbox__container`} onDragEnter={handleDragEnter} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
        <MessageContainer setRefreshConversationFn={setRefreshConversationFn} />
        {shouldRenderWarning && (
          <div className={`chatbox__file-warning-overlay ${showFileWarning ? "visible" : "fading-out"}`}>
            O Chatbot não permite enviar ficheiros, por favor, envie para o nosso email
          </div>
        )}
      </div>
    </div>
  );
};

export default Chatbox;
